import { useMemo } from 'react'
import type { RangeKey } from '@dataTypes/dashboard.types'
import type { PeriodComparisonMetric } from '@services/api/dashboard/finance.api'
import {
  getRevenueOverview,
  getRevenueOverviewOverview,
} from '@services/api/dashboard/operations.api'
import {
  buildComparisonFooter,
  computeChartAxis,
  filterYtdPeriodItems,
  financeTrendLine,
  formatCurrency,
  formatGoalCurrencyLabel,
  formatPercent,
  HYGIENE_PLACEHOLDER,
} from '@utils/dashboardFinance.util'
import type {
  DashboardMetricFooterTrend,
  DashboardMetricItemData,
} from '@utils/dashboardMetrics.util'
import {
  useDashboardPeriodFetch,
  useDashboardPeriodParams,
  type DashboardPeriodFetcher,
} from './useDashboardPeriod'
import { hasNonZeroDashboardValues } from '@utils/dashboardSectionEmpty.util'
import { useClinicGoal } from './useClinicGoal'

export type UseRevenueOptions = {
  range: RangeKey
  date: string
  enabled?: boolean
  queued?: boolean
}

export type RevenueMetricTabId =
  | 'collection'
  | 'income'
  | 'netProfit'
  | 'expenseRatio'

export type RevenueMetricTab = {
  id: RevenueMetricTabId
  leftLabel: string
  rightLabel?: string
  value: string
  footer: string
  footerTrend?: DashboardMetricFooterTrend
}

export type RevenueGrowthPoint = {
  label: string
  date?: string
  value: number
  isCurrent?: boolean
  isHighlight?: boolean
}

export type RevenueGrowthChartConfig = {
  data: RevenueGrowthPoint[]
  yDomain: [number, number]
  yTicks: number[]
  yTickFormatter: (v: number) => string
}

export type PayerMixBarPoint = {
  label: string
  value: number
  color: string
}

export type PayerMixChartConfig = {
  data: PayerMixBarPoint[]
  yDomain: [number, number]
  yTicks: number[]
  yTickFormatter: (v: number) => string
}

export type NetProfitCategoryPoint = {
  label: string
  value: number
  color: string
}

export type NetProfitCategoryChartConfig = {
  data: NetProfitCategoryPoint[]
  yDomain: [number, number]
  yTicks: number[]
  yTickFormatter: (v: number) => string
}

export type CollectionBreakdownTableRow = {
  id: string
  patientId: string
  patientName: string
  paymentMethod: string
  amount: string
  datetime: string
  postedBy: string
}

export type ProviderRankingTableRow = {
  id: string
  rank: string
  provider: string
  role: string
  production: string
}

export type NetProfitBreakdownTableRow = {
  id: string
  label: string
  amount: string
}

const EMPTY_METRIC: PeriodComparisonMetric = {
  current: 0,
  previous: 0,
  percentChange: 0,
}

const PAYER_MIX_COLORS = {
  patient: 'var(--blue-600)',
  insurance: 'var(--blue-400)',
} as const

const CATEGORY_BAR_COLORS = [
  'var(--blue-400)',
  'var(--orange-300)',
  'var(--teal-400)',
  'var(--vivid-green-400)',
  'var(--blue-600)',
]

function buildMetricFooter(
  range: RangeKey,
  metric: PeriodComparisonMetric,
  options?: { isCurrency?: boolean; isPercent?: boolean },
): { footer: string; footerTrend?: DashboardMetricFooterTrend } {
  const { up } = buildComparisonFooter(metric, {
    isCurrency: options?.isCurrency,
  })
  const pct = Math.abs(metric.percentChange)
  const sign = metric.percentChange >= 0 ? '+' : '-'
  const delta = metric.current - metric.previous
  const amt = options?.isPercent
    ? formatPercent(Math.abs(delta), 1)
    : formatCurrency(Math.abs(delta), { compact: true })
  return {
    footer: financeTrendLine(range, `${sign}${pct.toFixed(1)}%`, amt),
    footerTrend: up ? 'up' : 'down',
  }
}

function growthYTickFormatter(v: number): string {
  return `$${v}K`
}

function rankLabel(rank: number): string {
  const suffixes = ['th', 'st', 'nd', 'rd']
  const v = rank % 100
  const suffix = suffixes[(v - 20) % 10] || suffixes[v] || suffixes[0]
  return `${rank}${suffix}`
}

export type UseRevenueResult = {
  loading: boolean
  error: string | null
  /** Response has arrived (`data != null`); independent of nonzero content. */
  hasPayload: boolean
  hasData: boolean
  range: RangeKey
  cardMetrics: DashboardMetricItemData[]
  revenueMetricTabs: RevenueMetricTab[]
  growthChart: RevenueGrowthChartConfig
  payerMixChart: PayerMixChartConfig
  netProfitCategoryChart: NetProfitCategoryChartConfig
  collectionBreakdownRows: CollectionBreakdownTableRow[]
  providerRankingRows: ProviderRankingTableRow[]
  netProfitBreakdownRows: NetProfitBreakdownTableRow[]
}

function useRevenueFromFetcher({
  range,
  date,
  enabled = true,
  queued = false,
  fetcher,
}: UseRevenueOptions & {
  fetcher: DashboardPeriodFetcher<
    Awaited<ReturnType<typeof getRevenueOverview>>
  >
}): UseRevenueResult {
  const queryParams = useDashboardPeriodParams(range, date)
  const { data, loading, error } = useDashboardPeriodFetch(
    queryParams,
    fetcher,
    { enabled, queued },
  )
  const { goal: clinicGoal } = useClinicGoal({ range, date, enabled, queued })

  const summary = data?.summary

  const cardMetrics = useMemo((): DashboardMetricItemData[] => {
    const s = summary ?? {
      collection: EMPTY_METRIC,
      revenue: EMPTY_METRIC,
      netProfit: EMPTY_METRIC,
      expenseRatio: EMPTY_METRIC,
    }
    if (loading) {
      return [
        {
          id: 'collection',
          leftLabel: 'Collection',
          value: HYGIENE_PLACEHOLDER,
        },
        {
          id: 'income',
          leftLabel: 'Generated income',
          value: HYGIENE_PLACEHOLDER,
        },
        {
          id: 'netProfit',
          leftLabel: 'Net profit',
          value: HYGIENE_PLACEHOLDER,
        },
        {
          id: 'expenseRatio',
          leftLabel: 'Expense ratio',
          value: HYGIENE_PLACEHOLDER,
        },
      ]
    }
    const collectionGoal = formatGoalCurrencyLabel(
      clinicGoal?.gross_collection_goal,
    )
    const revenueGoal = formatGoalCurrencyLabel(
      clinicGoal?.practice_gross_collection_goal,
    )
    const netGoal = formatGoalCurrencyLabel(clinicGoal?.collection_header_goal)

    return [
      {
        id: 'collection',
        leftLabel: 'Collection',
        rightLabel: collectionGoal || undefined,
        value: formatCurrency(s.collection.current, { compact: true }),
        ...buildMetricFooter(range, s.collection, { isCurrency: true }),
      },
      {
        id: 'income',
        leftLabel: 'Generated income',
        rightLabel: revenueGoal || undefined,
        value: formatCurrency(s.revenue.current, { compact: true }),
        ...buildMetricFooter(range, s.revenue, { isCurrency: true }),
      },
      {
        id: 'netProfit',
        leftLabel: 'Net profit',
        rightLabel: netGoal || undefined,
        value: formatCurrency(s.netProfit.current, { compact: true }),
        ...buildMetricFooter(range, s.netProfit, { isCurrency: true }),
      },
      {
        id: 'expenseRatio',
        leftLabel: 'Expense ratio',
        value: formatPercent(s.expenseRatio.current, 1),
        ...buildMetricFooter(range, s.expenseRatio, { isPercent: true }),
      },
    ]
  }, [summary, loading, range, clinicGoal])

  const revenueMetricTabs = useMemo((): RevenueMetricTab[] => {
    return cardMetrics.map((m) => ({
      id: (m.id as RevenueMetricTabId) ?? 'collection',
      leftLabel: m.leftLabel,
      rightLabel: m.rightLabel,
      value: String(m.value),
      footer: String(m.footer ?? ''),
      footerTrend: m.footerTrend,
    }))
  }, [cardMetrics])

  const growthChart = useMemo((): RevenueGrowthChartConfig => {
    const buckets = filterYtdPeriodItems(
      range,
      date,
      data?.collectionPeriodSummary ?? [],
      (b) => b.label,
    )
    const chartData: RevenueGrowthPoint[] = buckets.map((b, i) => ({
      label: b.label,
      value: b.value / 1000,
      isCurrent: i === buckets.length - 1,
      isHighlight: i === buckets.length - 1,
    }))
    const values = chartData.map((p) => p.value)
    const axis = computeChartAxis(values)
    return {
      data: chartData,
      yDomain: axis.yDomain,
      yTicks: axis.yTicks,
      yTickFormatter: growthYTickFormatter,
    }
  }, [data, range, date])

  const payerMixChart = useMemo((): PayerMixChartConfig => {
    const breakdown = data?.revenueBreakdown ?? { patient: 0, insurance: 0 }
    const chartData: PayerMixBarPoint[] = [
      {
        label: 'Patient',
        value: breakdown.patient / 1000,
        color: PAYER_MIX_COLORS.patient,
      },
      {
        label: 'Insurance',
        value: breakdown.insurance / 1000,
        color: PAYER_MIX_COLORS.insurance,
      },
    ]
    const values = chartData.map((d) => d.value)
    const axis = computeChartAxis(values)
    return {
      data: chartData,
      yDomain: axis.yDomain,
      yTicks: axis.yTicks,
      yTickFormatter: growthYTickFormatter,
    }
  }, [data])

  const netProfitCategoryChart = useMemo((): NetProfitCategoryChartConfig => {
    const categories = data?.collectionByCategory ?? []
    const chartData: NetProfitCategoryPoint[] = categories.map((c, i) => ({
      label: c.category,
      value: c.value / 1000,
      color: CATEGORY_BAR_COLORS[i % CATEGORY_BAR_COLORS.length],
    }))
    const values = chartData.map((d) => d.value)
    const axis = computeChartAxis(values)
    return {
      data: chartData,
      yDomain: axis.yDomain,
      yTicks: axis.yTicks,
      yTickFormatter: growthYTickFormatter,
    }
  }, [data])

  const collectionBreakdownRows = useMemo((): CollectionBreakdownTableRow[] => {
    return (data?.collectionBreakdown ?? []).map((row, i) => ({
      id: String(i + 1),
      patientId: row.patientId,
      patientName: row.patientName,
      paymentMethod: row.paymentMethod,
      amount: formatCurrency(row.amount, { compact: true }),
      datetime: row.datetime,
      postedBy: row.postedBy,
    }))
  }, [data])

  const providerRankingRows = useMemo((): ProviderRankingTableRow[] => {
    return (data?.providerRanking ?? []).map((row) => ({
      id: String(row.rank),
      rank: rankLabel(row.rank),
      provider: row.provider,
      role: row.role,
      production: formatCurrency(row.production, { compact: true }),
    }))
  }, [data])

  const netProfitBreakdownRows = useMemo((): NetProfitBreakdownTableRow[] => {
    const b = data?.netProfitBreakdown
    if (!b) return []
    return [
      {
        id: '1',
        label: 'Gross',
        amount: formatCurrency(b.gross, { compact: true }),
      },
      {
        id: '2',
        label: 'Adjustment',
        amount: formatCurrency(b.adjustment, { compact: true }),
      },
      {
        id: '3',
        label: 'Net',
        amount: formatCurrency(b.net, { compact: true }),
      },
      {
        id: '4',
        label: 'Expenses',
        amount: formatCurrency(b.expenses, { compact: true }),
      },
    ]
  }, [data])

  return {
    loading,
    error,
    hasPayload: data != null,
    hasData: hasNonZeroDashboardValues(
      data?.summary.collection.current,
      data?.summary.revenue.current,
      data?.summary.netProfit.current,
      data?.summary.expenseRatio.current,
      data?.revenueBreakdown.patient,
      data?.revenueBreakdown.insurance,
    ),
    range,
    cardMetrics,
    revenueMetricTabs,
    growthChart,
    payerMixChart,
    netProfitCategoryChart,
    collectionBreakdownRows,
    providerRankingRows,
    netProfitBreakdownRows,
  }
}

export function useRevenue(options: UseRevenueOptions): UseRevenueResult {
  return useRevenueFromFetcher({ ...options, fetcher: getRevenueOverview })
}

export function useRevenueOverview(
  options: UseRevenueOptions,
): UseRevenueResult {
  return useRevenueFromFetcher({
    ...options,
    fetcher: getRevenueOverviewOverview,
  })
}
